export const domainOfEmail = (email: string): string | null => {
	const domain = email.split("@")[1]?.trim().toLowerCase()
	return domain || null
}

export const localPartOfEmail = (email: string): string | null => {
	const [localPart, domain] = email.split("@")
	if (!domain) return null
	return localPart?.trim().toLowerCase() || null
}

const EMAIL_ATOM = String.raw`[^\s@"(),:;<>[\]\\.]+`
const DOMAIN_LABEL = String.raw`[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?`
const TOP_LEVEL_DOMAIN = String.raw`(?:\p{L}{2,63}|xn--[a-z0-9-]{1,59})`
const EMAIL_ADDRESS_PATTERN = new RegExp(
	`^${EMAIL_ATOM}(?:\\.${EMAIL_ATOM})*@(?:${DOMAIN_LABEL}\\.)+${TOP_LEVEL_DOMAIN}$`,
	"iu",
)

// Postmark rejects the whole message when one recipient fails its parser
// ("Illegal email domain 'acme.co.uk.'"), so nobody on the email receives it.
export const isValidEmailAddress = (email: string): boolean => {
	if (email.length > 254) return false
	const localPart = email.slice(0, email.lastIndexOf("@"))
	if (localPart.length > 64) return false
	return EMAIL_ADDRESS_PATTERN.test(email)
}

export const invalidRecipientEmails = (
	recipients: readonly { email: string }[],
): string[] =>
	recipients
		.map((recipient) => recipient.email)
		.filter((email) => !isValidEmailAddress(email))

export const normalizeDomain = (domain: string): string =>
	domain.trim().toLowerCase().replace(/\.$/, "")

// "notifications.acme.com" is under "acme.com"; "notacme.com" and
// "acme.com.evil.com" are not.
export const isSubdomainOf = (candidate: string, root: string): boolean => {
	const c = normalizeDomain(candidate)
	const r = normalizeDomain(root)
	if (!c || !r || c === r) return false
	return c.endsWith(`.${r}`)
}

export const isOnDomain = (email: string, domain: string): boolean => {
	const emailDomain = domainOfEmail(email)
	return !!emailDomain && emailDomain === normalizeDomain(domain)
}

export type EmailAddressValidationError =
	| "malformed"
	| "wrong-domain"
	| "wrong-broadcast-domain"

// An address is legal on any domain the workspace has verified, so adding a
// second organizational domain is what unlocks addresses on it.
export const validateAddressDomain = ({
	email,
	verifiedDomains,
}: {
	email: string
	verifiedDomains: readonly string[]
}): EmailAddressValidationError | null => {
	const domain = domainOfEmail(email)
	if (!domain || !localPartOfEmail(email)) return "malformed"
	if (!verifiedDomains.map(normalizeDomain).includes(domain))
		return "wrong-domain"
	return null
}

// Bulk sending gets its own subdomain so its reputation is separate from the
// domain the support address depends on.
export const validateBroadcastDomain = ({
	domain,
	rootDomain,
}: {
	domain: string
	rootDomain: string
}): EmailAddressValidationError | null => {
	const candidate = normalizeDomain(domain)
	if (!candidate || candidate.includes("@") || !candidate.includes("."))
		return "malformed"
	if (!isSubdomainOf(candidate, rootDomain)) return "wrong-broadcast-domain"
	return null
}

// A domain we are about to register with Postmark. Any real hostname is fine;
// the subdomain rule only applies to the dedicated broadcast domain.
export const validateSendingDomain = (
	domain: string,
): EmailAddressValidationError | null => {
	const candidate = normalizeDomain(domain)
	if (!candidate || candidate.includes("@") || !candidate.includes("."))
		return "malformed"
	if (!/^[a-z0-9.-]+$/.test(candidate)) return "malformed"
	return null
}

export const defaultBroadcastDomain = (rootDomain: string): string =>
	`notifications.${normalizeDomain(rootDomain)}`
